import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDragScroll } from "@/hooks/useDragScroll";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TableCard from "@/components/TableCard";
import CreateTableDialog from "@/components/CreateTableDialog";
import CreditsModal from "@/components/CreditsModal";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Filter, MapPin, Info, Dices } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const VENUES = ["all", "Bibliocronache", "Patrogames"];


const VENUE_LIMITS = {
  Bibliocronache: 8,
  Patrogames: 10,
};

export default function Dashboard() {
  const venueScroll = useDragScroll();
  const categoryScroll = useDragScroll();
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [achievement, setAchievement] = useState(null);
  
  // Banner state
  const [banner, setBanner] = useState({ text: "", active: false });
  const [editBannerText, setEditBannerText] = useState("");
  const [isEditingBanner, setIsEditingBanner] = useState(false);

  // Marquee state
  const [marquee, setMarquee] = useState("I tavoli vengono azzerati ogni mercoledì alle 04:00 (Bibliocronache) e ogni sabato alle 04:00 (Patrogames) — pianifica di conseguenza.");
  const [editMarqueeText, setEditMarqueeText] = useState("");
  const [isEditingMarquee, setIsEditingMarquee] = useState(false);

  const navigate = useNavigate();
  const pendingToastShown = useRef(false);

  useEffect(() => { document.title = "Tavoli da Gioco — Trova un tavolo"; }, []);

  useEffect(() => {
    if (!user?.id || (user.role !== "admin" && user.role !== "superadmin")) return;
    if (pendingToastShown.current) return;
    pendingToastShown.current = true;

    api.get("/admin/users").then(({ data }) => {
      const pending = data.filter((u) => !u.approved).length;
      if (pending > 0) {
        toast(`${pending} ${pending === 1 ? "utente in attesa" : "utenti in attesa"} di approvazione`, {
          description: "Vai al pannello admin per approvare le richieste.",
          duration: 12000,
          action: {
            label: "Vai all'admin",
            onClick: () => navigate("/admin"),
          },
        });
      }
    }).catch(() => {});
  }, [user?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.category = filter;
      if (venueFilter !== "all") params.venue = venueFilter;
      const { data } = await api.get("/tables", { params });
      // tavoli senza data prima (ordine naturale), poi con data futura in coda
      data.sort((a, b) => {
        const aHasDate = !!a.game_date;
        const bHasDate = !!b.game_date;
        if (aHasDate !== bHasDate) return aHasDate ? 1 : -1;
        if (aHasDate && bHasDate) return a.game_date.localeCompare(b.game_date);
        return 0;
      });
      setTables(data);
    } finally {
      setLoading(false);
    }
  }, [filter, venueFilter]);

  useEffect(() => {
    api.get("/categories").then(res => {
      setAvailableCategories(res.data?.categories || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // Load banner
    api.get("/settings/banner").then(res => {
      if(res.data) {
        setBanner(res.data);
        setEditBannerText(res.data.text);
      }
    }).catch(() => {});
    // Load marquee
    api.get("/settings/marquee").then(res => {
      if (res.data?.text) {
        setMarquee(res.data.text);
        setEditMarqueeText(res.data.text);
      }
    }).catch(() => {});
  }, [load]);

  const saveMarquee = async () => {
    try {
      const res = await api.post("/admin/marquee", { text: editMarqueeText });
      setMarquee(res.data.text);
      setIsEditingMarquee(false);
      toast.success("Testo scorrevole aggiornato!");
    } catch(err) {
      toast.error("Errore salvataggio testo scorrevole");
    }
  };

  const saveBanner = async (active) => {
    try {
      const res = await api.post("/admin/banner", { text: editBannerText, active });
      setBanner(res.data);
      setIsEditingBanner(false);
      toast.success(active ? "Banner pubblicato!" : "Banner rimosso!");
    } catch(err) {
      toast.error("Errore salvataggio banner");
    }
  };

  const showAchievement = useCallback((title, description) => {
    setAchievement({ title, description });
    window.clearTimeout(window.__achievementTimer);
    window.__achievementTimer = window.setTimeout(() => {
      setAchievement(null);
    }, 5000);
  }, []);

  const myTables = tables.filter((t) => t.players?.some((p) => p.id === user.id));
  const myCreatedTables = tables.filter((t) => t.creator?.id === user.id);
  const totalParticipants = myCreatedTables.reduce((sum, t) => sum + (t.players?.length || 0), 0);
  const hoursJoined = Math.round(myTables.reduce((sum, t) => sum + (t.play_time || 0), 0) / 60 * 10) / 10;
  const hoursOrganized = Math.round(myCreatedTables.reduce((sum, t) => sum + (t.play_time || 0), 0) / 60 * 10) / 10;

  const venueCounts = {
    Bibliocronache: tables.filter((t) => t.venue === "Bibliocronache").length,
    Patrogames: tables.filter((t) => t.venue === "Patrogames").length,
  };

  const venueAvailability = {
    Bibliocronache: {
      count: venueCounts.Bibliocronache,
      limit: VENUE_LIMITS.Bibliocronache,
      full: venueCounts.Bibliocronache >= VENUE_LIMITS.Bibliocronache,
    },
    Patrogames: {
      count: venueCounts.Patrogames,
      limit: VENUE_LIMITS.Patrogames,
      full: venueCounts.Patrogames >= VENUE_LIMITS.Patrogames,
    },
  };

  const allVenuesFull =
    venueAvailability.Bibliocronache.full && venueAvailability.Patrogames.full;

  useEffect(() => {
    if (!user) return;

    const joinedTables = tables.filter((t) =>
      t.players?.some((p) => p.id === user.id)
    );
    const createdTables = tables.filter((t) => t.creator?.id === user.id);

    if (
      createdTables.length >= 4 &&
      !localStorage.getItem("achievement_dungeon_master")
    ) {
      localStorage.setItem("achievement_dungeon_master", "true");
      showAchievement("Dungeon Master", "Hai creato 4 tavoli!");
      return;
    }

    if (
      joinedTables.length >= 4 &&
      !localStorage.getItem("achievement_meeple_collector")
    ) {
      localStorage.setItem("achievement_meeple_collector", "true");
      showAchievement("Collezionista di Meeple", "Partecipi a 4 tavoli!");
    }
  }, [tables, user, showAchievement]);

  const handleTestAchievement = () => {
    showAchievement("Dungeon Master", "Hai creato 4 tavoli!");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818] grain flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <section className="flex flex-col items-center justify-center text-center mb-14 mt-6 relative w-full">
          <div className="w-full overflow-hidden absolute -top-4 left-0 right-0 max-w-7xl mx-auto h-6 group">
            {!isEditingMarquee ? (
              <div className="animate-marquee whitespace-nowrap flex items-center gap-2.5 text-xs text-[#757169] dark:text-[#C8C2BA]">
                <Info size={14} className="text-[#C8553D] dark:text-[#F08A78]" />
                <span dangerouslySetInnerHTML={{ __html: marquee }} />
              </div>
            ) : null}
          </div>
          
          {/* BANNER GLOBALE */}
          {banner.active && !isEditingBanner && (
             <div className="w-full max-w-4xl mx-auto mt-6 p-4 bg-[#A6432E] dark:bg-[#7A2020] text-white rounded-xl shadow-lg font-bold text-sm md:text-base animate-in slide-in-from-top-4 flex items-center justify-center gap-3">
               <Info size={20} />
               <span>{banner.text}</span>
             </div>
          )}

          {(user?.role === "admin" || user?.role === "superadmin") && (
            <div className="w-full max-w-4xl mx-auto mt-4 flex flex-col gap-2">
               {/* BANNER EDIT */}
               {isEditingBanner ? (
                  <div className="bg-white/80 dark:bg-[#111]/80 backdrop-blur border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl p-3 flex flex-col sm:flex-row gap-2 shadow-sm animate-in fade-in">
                     <input type="text" value={editBannerText} onChange={e => setEditBannerText(e.target.value)} placeholder="Scrivi un avviso (es. Sede chiusa per neve...)" className="flex-1 bg-transparent text-sm font-semibold text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none placeholder:text-[#C0BAB2]" />
                     <div className="flex gap-2 justify-end mt-2 sm:mt-0">
                       <button onClick={() => saveBanner(true)} className="px-3 py-1.5 bg-[#2B593F] text-white rounded-lg text-xs font-bold hover:bg-[#1D3C2A] transition-colors">Pubblica</button>
                       <button onClick={() => saveBanner(false)} className="px-3 py-1.5 bg-[#C8553D] text-white rounded-lg text-xs font-bold hover:bg-[#A6432E] transition-colors">Nascondi</button>
                       <button onClick={() => setIsEditingBanner(false)} className="px-3 py-1.5 bg-[#F5F1EA] dark:bg-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] rounded-lg text-xs font-bold hover:bg-[#EAE3D9] dark:hover:bg-[#333] transition-colors">Annulla</button>
                     </div>
                  </div>
               ) : null}

               {/* MARQUEE EDIT */}
               {isEditingMarquee ? (
                  <div className="bg-white/80 dark:bg-[#111]/80 backdrop-blur border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl p-3 flex flex-col sm:flex-row gap-2 shadow-sm animate-in fade-in">
                     <input type="text" value={editMarqueeText} onChange={e => setEditMarqueeText(e.target.value)} placeholder="Testo scorrevole in alto..." className="flex-1 bg-transparent text-sm font-semibold text-[#2D2A26] dark:text-[#F5F1EA] focus:outline-none placeholder:text-[#C0BAB2]" />
                     <div className="flex gap-2 justify-end mt-2 sm:mt-0">
                       <button onClick={() => saveMarquee()} className="px-3 py-1.5 bg-[#2B593F] text-white rounded-lg text-xs font-bold hover:bg-[#1D3C2A] transition-colors">Salva Testo Scorrevole</button>
                       <button onClick={() => setIsEditingMarquee(false)} className="px-3 py-1.5 bg-[#F5F1EA] dark:bg-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] rounded-lg text-xs font-bold hover:bg-[#EAE3D9] dark:hover:bg-[#333] transition-colors">Annulla</button>
                     </div>
                  </div>
               ) : null}

               {(!isEditingBanner && !isEditingMarquee) && (
                 <div className="flex items-center justify-center gap-4 mt-2">
                   <button onClick={() => setIsEditingBanner(true)} className="text-[10px] uppercase tracking-widest font-bold text-[#C8553D] dark:text-[#F08A78] hover:underline flex items-center justify-center gap-1"><Info size={12}/> Gestisci Avviso Globale</button>
                   <button onClick={() => { setIsEditingMarquee(true); setEditMarqueeText(marquee); }} className="text-[10px] uppercase tracking-widest font-bold text-[#757169] dark:text-[#B8B2AA] hover:text-[#2D2A26] dark:hover:text-[#F5F1EA] hover:underline flex items-center justify-center gap-1">Gestisci Testo Scorrevole</button>
                 </div>
               )}
            </div>
          )}
          
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight leading-[1.1] mt-8">
            Trova un tavolo, <span className="text-[#2B593F] dark:text-[#8FC79F]">o creane uno tuo.</span>
          </h2>

          <div className="mt-10 flex flex-col items-center">
            <CreateTableDialog
              onSaved={load}
              venueAvailability={venueAvailability}
              disabled={allVenuesFull}
            />
            {allVenuesFull && (
              <div className="text-sm text-[#C8553D] dark:text-[#F08A78] mt-3">
                Limite tavoli raggiunto per tutte le sedi
              </div>
            )}
          </div>
        </section>



        {/* Compact Filters Toolbar */}
        <section className="mb-8 sticky top-[72px] z-30">
          <div className="bg-white/85 dark:bg-[#151515]/85 backdrop-blur-xl border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-1.5 shadow-sm flex flex-col md:flex-row md:items-center gap-2">
            
            {/* Sede Filters */}
            <div 
              {...venueScroll}
              className="flex items-center gap-1 bg-[#FDFBF7] dark:bg-[#1A1A1A] p-1 rounded-xl border border-[#EAE3D9] dark:border-[#3A3A3A] flex-shrink-0 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex items-center gap-1.5 pl-2 pr-1">
                <MapPin size={14} className="text-[#757169] dark:text-[#B8B2AA]" />
              </div>
              {VENUES.map((v) => {
                const availability = venueAvailability[v];
                const isFull = availability?.full;
                return (
                  <button
                    key={v}
                    onClick={() => setVenueFilter(v)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      venueFilter === v
                        ? "bg-[#C8553D] text-[#FDFBF7] shadow-sm"
                        : "text-[#757169] dark:text-[#B8B2AA] hover:bg-[#F5F1EA] dark:hover:bg-[#252525] hover:text-[#2D2A26] dark:hover:text-[#F5F1EA]"
                    }`}
                  >
                    {v === "all" ? "Tutte le Sedi" : `${v} ${availability ? `(${availability.count}/${availability.limit})` : ""}${isFull ? " 🔒" : ""}`}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-[#EAE3D9] dark:bg-[#3A3A3A] mx-1"></div>

            {/* Category Filters */}
            <div 
              {...categoryScroll}
              className="flex flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex items-center gap-1.5 pl-1 pr-1 flex-shrink-0">
                <Filter size={14} className="text-[#757169] dark:text-[#B8B2AA] hidden sm:block" />
              </div>
              <div className="flex gap-1">
                {["all", ...availableCategories].map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                      filter === c
                        ? "bg-[#2B593F] text-[#FDFBF7] border-[#2B593F] shadow-sm"
                        : "bg-transparent text-[#757169] dark:text-[#B8B2AA] border-transparent hover:border-[#EAE3D9] dark:hover:border-[#3A3A3A] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] hover:text-[#2D2A26] dark:hover:text-[#F5F1EA]"
                    }`}
                  >
                    {c === "all" ? "Tutte le Categorie" : c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tables grid */}
        <section data-testid="tables-grid">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#34312C] rounded-2xl p-6 animate-pulse h-72"
                >
                  <div className="h-3 w-20 bg-[#F5F1EA] dark:bg-[#252525] rounded-full" />
                  <div className="h-6 w-3/4 bg-[#F5F1EA] dark:bg-[#252525] rounded mt-4" />
                  <div className="grid grid-cols-3 gap-2 mt-6">
                    <div className="h-16 bg-[#F5F1EA] dark:bg-[#252525] rounded-xl" />
                    <div className="h-16 bg-[#F5F1EA] dark:bg-[#252525] rounded-xl" />
                    <div className="h-16 bg-[#F5F1EA] dark:bg-[#252525] rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#EAE3D9] dark:border-[#34312C] rounded-3xl bg-[#FDFBF7] dark:bg-[#0A0A0A]">
              <Dices size={80} className="mx-auto text-[#C8BAA8] dark:text-[#4A4540] opacity-80" strokeWidth={1} />
              <h3 className="font-display text-2xl font-semibold text-[#2D2A26] dark:text-[#F5F1EA] mt-4">
                Nessun tavolo {filter !== "all" ? `per ${filter}` : "ancora"}
              </h3>
              <p className="text-[#757169] dark:text-[#B8B2AA] mt-2">
                Sii il primo a creare un tavolo per la community.
              </p>
              <div className="mt-6 inline-block">
                <CreateTableDialog
                  onSaved={load}
                  venueAvailability={venueAvailability}
                  disabled={allVenuesFull}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tables.map((t) => (
                <TableCard key={t.id} table={t} onChanged={load} />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer onCreditsOpen={() => setCreditsOpen(true)} showTestAchievement={user?.role === "superadmin"} onTestAchievement={handleTestAchievement} />

      {achievement && (
        <div className="fixed bottom-5 right-5 z-50 max-w-[calc(100vw-2.5rem)]">
          <div className="bg-[#1E1E1E] border border-[#2B593F] rounded-2xl shadow-2xl px-5 py-4 w-[320px] max-w-full">
            <div className="text-[#8FC79F] font-semibold text-sm">
              Achievement sbloccato
            </div>
            <div className="text-white font-bold mt-1">
              {achievement.title}
            </div>
            <div className="text-[#C8C2BA] text-sm mt-1">
              {achievement.description}
            </div>
          </div>
        </div>
      )}

      <CreditsModal open={creditsOpen} onClose={() => setCreditsOpen(false)} />
    </div>
  );
}
